// The Steam trade history, readable from anywhere under SkinTable.
//
// WHY A CONTEXT
// Same reason as settlingSales.js: ItemDetailModal is opened from fifteen tabs,
// and the trade chain inside it is the one place that needs the raw history.
// It needs it for the side of a trade the tracker never had a row for. A trade
// that brought items in keeps its received rows, but the item you HANDED OVER
// only has a row if it was tracked before it left. When it was not, the chain
// used to say "item no longer in the tracker" while the history on the account
// still named it.
//
// Nothing here writes. Without a provider every lookup answers null, which is
// exactly what the chain showed before this existed.

import React, { createContext, useContext } from 'react';

const TradeHistoryContext = createContext(null);

export function TradeHistoryProvider({ events, children }) {
  return <TradeHistoryContext.Provider value={events || null}>{children}</TradeHistoryContext.Provider>;
}

export function useTradeHistory() {
  return useContext(TradeHistoryContext);
}

const partyName = p => (typeof p === 'string' ? p : p?.name || p?.steamId64 || '');

/**
 * The Steam trade event one hop of the chain was built from, or null.
 *
 * Trade ids on rows are minted by the server (makeTradeId), not read off Steam,
 * so there is no id to join on. What the linker does copy is the moment: every
 * row it creates for a trade carries the event's timestamp, and the counterparty
 * with it. Same second, same person is the same trade. When the time alone is
 * ambiguous and no partner settles it, nothing is returned rather than a guess.
 */
export function findTradeEvent(events, hop) {
  if (!events?.length || !hop) return null;
  const rows  = [...(hop.received || []), ...(hop.gave || [])].map(e => e?.row).filter(Boolean);
  const times = new Set(
    [hop.at, ...rows.map(r => r.tradedAt), ...rows.map(r => r.timestamp)]
      .map(t => Date.parse(t || ''))
      .filter(Number.isFinite),
  );
  if (!times.size) return null;

  const hits = events.filter(e => e?.itemsGiven?.length && times.has(Date.parse(e.timestamp || '')));
  if (hits.length <= 1) return hits[0] || null;

  const partner = partyName(hop.partner) || partyName(rows.find(r => r.tradedWith)?.tradedWith);
  const byPartner = partner ? hits.filter(e => partyName(e.tradedWith) === partner) : [];
  return byPartner.length === 1 ? byPartner[0] : null;
}
